/*!
 * PAWMART CanvasUI care-theatre adapter.
 * Adapted from Canvas UI's BlazeVanilla, AsciifyVanilla,
 * DropletsVanilla, and ParticleScrollVanilla components:
 * https://github.com/DavidHDev/canvas-ui/tree/main/src/lib
 * Copyright (c) David H. and Canvas UI contributors.
 * Licensed under the MIT License with the Canvas UI Commons Clause.
 * PAWMART's real Shopify media and controls stay visible and interactive;
 * these canvases are transparent, decorative enhancements that fail open.
 */
(() => {
  if (window.PMXCanvasUIScenes) return;

  const ROOT_SELECTOR = '[data-pmx-h6-front][data-pmx-h6-hero-concept="canvasui-ripple"]';
  const instances = new Map();
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MYSTIC_GLYPHS = ['?', '∴', '∵', 'Σ', '≈', '<', '>', '+', '=', '/', '\\', '0', '1', '*'];
  const MODE_SWEEP_GLYPHS = {
    dog: ['+', '×', '/', '•', '◇', '>'],
    cat: MYSTIC_GLYPHS,
    grooming: ['○', '◌', '◦', '∿', '·', '≈'],
  };

  const VERTEX_SHADER = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUv;
void main () {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

  // One transparent shader hosts three bounded CanvasUI-derived treatments:
  // Blaze for dog-supply energy, Asciify for feline discovery, and Droplets
  // for the wash-and-finish service. The photograph remains normal HTML.
  const FRAGMENT_SHADER = `#version 300 es
precision highp float;
precision highp int;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uContent;
uniform float uHasContent;
uniform vec2 uResolution;
uniform vec2 uPointer;
uniform float uPointerActive;
uniform float uTime;
uniform int uMode;
uniform vec2 uCoverScale;
uniform vec2 uCoverOffset;

float hash21 (vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise21 (vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash21(i), hash21(i + vec2(1.0, 0.0)), f.x),
    mix(hash21(i + vec2(0.0, 1.0)), hash21(i + 1.0), f.x), f.y);
}

vec3 hash13 (float p) {
  vec3 p3 = fract(vec3(p) * vec3(0.1031, 0.11369, 0.13787));
  p3 += dot(p3, p3.yzx + 19.19);
  return fract(vec3(
    (p3.x + p3.y) * p3.z,
    (p3.x + p3.z) * p3.y,
    (p3.y + p3.z) * p3.x
  ));
}

float hash11 (float p) {
  return fract(sin(p * 12345.564) * 7658.76);
}

float sawPulse (float shoulder, float t) {
  return smoothstep(0.0, shoulder, t) * (1.0 - smoothstep(shoulder, 1.0, t));
}

float sdEgg (vec2 p, float ra, float rb) {
  const float k = 1.7320508;
  p.x = abs(p.x);
  float r = ra - rb;
  return ((p.y < 0.0) ? length(p) - r
    : (k * (p.x + r) < p.y) ? length(vec2(p.x, p.y - k * r))
    : length(vec2(p.x + r, p.y)) - 2.0 * r) - rb;
}

vec3 photo (vec2 uv) {
  vec2 pageUv = vec2(uv.x, 1.0 - uv.y);
  vec2 texUv = clamp(uCoverOffset + pageUv * uCoverScale, vec2(0.001), vec2(0.999));
  return texture(uContent, texUv).rgb;
}

float glyphBit (int glyph, ivec2 p) {
  if (p.x < 0 || p.x > 4 || p.y < 0 || p.y > 4) return 0.0;
  // PAWMART's feline ramp: space, dot, ?, +, =, /, \\, <, >, *, 0, 1, #, ∴.
  // These compact 5x5 masks keep the CanvasUI Asciify idea legible without
  // replacing or rasterising the real Shopify content underneath.
  uint bits[14] = uint[14](
    0u, 4325376u, 4327982u, 4357252u, 1016800u, 17043521u, 1118480u,
    2236546u, 8521864u, 22511061u, 15521390u, 14815620u, 11512810u, 327808u
  );
  return float((bits[glyph] >> uint((4 - p.x) + 5 * p.y)) & 1u);
}

vec4 blaze () {
  vec2 uv = vUv;
  float aspect = uResolution.x / max(uResolution.y, 1.0);
  float n = noise21(vec2(uv.x * 7.0, uv.y * 5.0 - uTime * 1.4));
  n += noise21(vec2(uv.x * 15.0 + 2.0, uv.y * 9.0 - uTime * 2.2)) * 0.42;
  float edge = min(uv.x, 1.0 - uv.x);
  float bed = (1.0 - smoothstep(0.0, 0.24 + n * 0.08, uv.y)) * smoothstep(0.0, 0.09, edge);
  float side = (1.0 - smoothstep(0.01, 0.11, edge)) * (1.0 - smoothstep(0.08, 0.82, uv.y)) * (0.35 + n * 0.65);
  vec2 sparkCell = floor(vec2(uv.x * 48.0 * aspect, uv.y * 34.0));
  float seed = hash21(sparkCell);
  float rise = fract(seed + uTime * (0.12 + seed * 0.24));
  float spark = step(0.965, seed) * (1.0 - smoothstep(0.0, 0.035, abs(uv.y - rise * 0.72)));
  spark *= 1.0 - smoothstep(0.0, 0.48, abs(uv.x - fract(seed * 8.3)));
  float fire = clamp(bed + side + spark * 0.9, 0.0, 1.0);
  vec3 amber = mix(vec3(0.95, 0.24, 0.03), vec3(1.0, 0.72, 0.16), n);
  float alpha = fire * 0.32;
  return vec4(amber * alpha, alpha);
}

vec4 asciify () {
  float cellPx = 14.0;
  vec2 frag = vUv * uResolution;
  vec2 cell = floor(frag / cellPx);
  vec2 cellCenter = (cell + 0.5) * cellPx / uResolution;
  vec2 local = floor(fract(frag / cellPx) * 7.0) - 1.0;
  float aspect = uResolution.x / max(uResolution.y, 1.0);
  vec2 pointer = mix(vec2(0.56, 0.52), uPointer, uPointerActive);
  float dist = length((cellCenter - pointer) * vec2(aspect, 1.0));
  float lens = 1.0 - smoothstep(0.10, 0.34, dist);
  float calculationBand = 1.0 - smoothstep(0.035, 0.22, abs(cellCenter.x - pointer.x));
  float base = 0.035 + hash21(cell) * 0.035;
  vec3 source = uHasContent > 0.5 ? photo(cellCenter) : vec3(hash21(cell));
  float lum = dot(source, vec3(0.299, 0.587, 0.114));
  float cellSeed = hash21(cell + floor(uTime * 0.42));
  int glyph = int(clamp(floor(mix(lum, cellSeed, 0.72) * 13.99), 0.0, 13.0));
  float bit = glyphBit(glyph, ivec2(local));
  float activity = max(lens, calculationBand * (0.48 + cellSeed * 0.32));
  float coverage = max(activity, base) * bit;
  float colorMoment = step(0.94, fract(cellSeed + uTime * 0.37));
  vec3 quietInk = mix(vec3(0.55, 0.76, 0.78), vec3(0.94, 0.93, 0.76), lum);
  vec3 ink = mix(quietInk, vec3(0.95, 0.54, 0.18), colorMoment * (0.45 + activity * 0.55));
  float glow = coverage * (0.36 + activity * 0.52);
  return vec4(ink * glow, glow);
}

vec2 dropletLayer (vec2 uv, float t) {
  vec2 sourceUv = uv;
  vec2 cellAspect = vec2(6.0, 1.0);
  vec2 grid = cellAspect * 2.0;
  vec2 id = floor(uv * grid);
  float fallRate = hash11(id.x) / 3.0 + 0.5;
  uv.y += t * fallRate;
  id = floor(uv * grid);
  uv.y += hash11(id.x);

  id = floor(uv * grid);
  vec2 cell = fract(uv * grid) - vec2(0.5, 0.0);
  vec3 random = hash13(id.x * 35.2 + id.y * 2376.1);
  float x = (random.x - 0.5) * 0.62;
  float wave = sourceUv.y * 20.0;
  x += sin(wave + sin(wave)) * (0.5 - abs(x)) * (random.z - 0.5) * 0.42;

  float slowStart = 0.85;
  float cycle = fract(t * (fallRate + 0.1) + random.z);
  float y = (sawPulse(slowStart, cycle) - 0.5) * 0.9 + 0.5;
  float stretch = cycle > slowStart
    ? -sin(6.2831853 * cycle / (1.0 - slowStart)) * 0.5 - 0.5
    : 0.0;
  float eggDistance = sdEgg((cell - vec2(x, y)) * cellAspect.yx / vec2(0.82, 1.12), 0.0, stretch);
  float diameter = hash11(id.x + id.y) / 7.0 + 0.2;
  float mainDrop = 1.0 - smoothstep(0.0, diameter / 1.5, eggDistance);

  float trailRadius2 = 1.0 - smoothstep(y, 1.0, cell.y);
  float trailRadius = sqrt(max(trailRadius2, 0.0));
  float columnDistance = abs(cell.x - x);
  float thickness = diameter * 0.78;
  float trail = 1.0 - smoothstep(0.0, thickness * trailRadius, columnDistance);
  float behindDrop = smoothstep(-0.02, 0.02, cell.y - y);
  trail *= trailRadius2 * behindDrop * 0.32;

  float beadLane = (1.0 - smoothstep(0.0, max((thickness - 0.1) * trailRadius, 0.01), columnDistance));
  beadLane *= behindDrop * random.z;
  float beadY = fract(sourceUv.y * (8.0 + hash11(id.x) * 5.0)) + (cell.y - 0.5);
  float beadDistance = length(cell - vec2(x, beadY));
  float bead = 1.0 - smoothstep(0.0, beadLane * 0.12 + 0.035, beadDistance);

  return vec2(max(mainDrop, bead * trailRadius), trail);
}

float staticDroplets (vec2 uv, float t) {
  uv *= 34.0;
  vec2 id = floor(uv);
  vec3 random = hash13(id.x * 107.45 + id.y * 3543.654);
  vec2 point = (random.xy - 0.5) * 0.58;
  float distanceToDrop = length(fract(uv) - 0.5 - point);
  float drop = 1.0 - smoothstep(0.0, 0.22, distanceToDrop);
  float fade = sawPulse(0.1, fract(t + random.y));
  return drop * fade * fract(random.x * 27.0);
}

vec2 dropletField (vec2 uv, float t, float wipe) {
  float staticField = staticDroplets(uv, t * 0.2) * 0.32 * wipe;
  vec2 large = dropletLayer(uv, t * 0.16) * wipe;
  vec2 small = dropletLayer(uv * 1.85, t * 0.16) * wipe;
  float body = smoothstep(0.16, 0.82, staticField + large.x * 0.92 + small.x * 0.72);
  return vec2(body, large.y + small.y);
}

vec4 droplets () {
  vec2 uv = vUv;
  float aspect = uResolution.x / max(uResolution.y, 1.0);
  vec2 glassUv = (uv - 0.5) * vec2(aspect, 1.0) * 1.08;
  vec2 pointer = mix(vec2(-2.0), uPointer, uPointerActive);
  float wipeDistance = length((uv - pointer) * vec2(aspect, 1.0));
  float wipe = smoothstep(0.16, 0.28, wipeDistance);
  vec2 water = dropletField(glassUv, uTime, wipe);

  vec2 epsilon = vec2(0.0018, 0.0);
  float fieldX = dropletField(glassUv + epsilon, uTime, wipe).x;
  float fieldY = dropletField(glassUv + epsilon.yx, uTime, wipe).x;
  vec2 normal = vec2(fieldX - water.x, fieldY - water.x);
  vec2 refractedUv = clamp(uv + normal * 0.16, vec2(0.001), vec2(0.999));
  vec3 refracted = uHasContent > 0.5 ? photo(refractedUv) : vec3(0.72, 0.88, 0.92);

  vec3 surfaceNormal = normalize(vec3(normal * 48.0, 1.0));
  vec3 light = normalize(vec3(-0.35, 0.75, 0.55));
  float specular = pow(max(dot(reflect(vec3(0.0, 0.0, -1.0), surfaceNormal), light), 0.0), 34.0);
  float rim = clamp(length(normal) * 32.0, 0.0, 1.0);
  float mask = smoothstep(0.02, 0.18, water.x);
  vec3 glass = mix(refracted, vec3(0.78, 0.93, 0.96), 0.08 + rim * 0.16) + vec3(specular * 0.72);
  float alpha = mask * clamp(0.13 + rim * 0.46 + specular * 0.76, 0.0, 0.68);
  return vec4(clamp(glass, 0.0, 1.0) * alpha, alpha);
}

void main () {
  if (uMode == 1) outColor = asciify();
  else if (uMode == 2) outColor = droplets();
  else outColor = blaze();
}`;

  function compileShader(gl, type, source) {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function createParticleScroll(root) {
    const canvas = document.createElement('canvas');
    canvas.className = 'pmx-canvasui-particle-scroll';
    canvas.dataset.pmxCanvasuiParticleScroll = 'ready';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);
    const context = canvas.getContext('2d', { alpha: true });
    if (!context) {
      canvas.remove();
      return { destroy() {} };
    }

    const particles = Array.from({ length: 84 }, (_, index) => ({
      side: index % 2,
      lane: (index % 7) / 6,
      phase: ((index * 47) % 83) / 83,
      size: 0.7 + ((index * 17) % 10) / 10,
    }));
    let frame = 0;
    let destroyed = false;

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.max(1, Math.round(window.innerWidth * dpr));
      canvas.height = Math.max(1, Math.round(window.innerHeight * dpr));
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function render() {
      frame = 0;
      if (destroyed) return;
      context.clearRect(0, 0, window.innerWidth, window.innerHeight);
      if (motionQuery.matches) return;
      const scrollRange = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      const progress = Math.min(Math.max(window.scrollY / scrollRange, 0), 1);
      const formationLine = window.innerHeight * (0.2 + progress * 0.6);
      canvas.dataset.pmxCanvasuiScrollProgress = progress.toFixed(3);
      root.dataset.pmxCanvasuiParticleScroll = 'active';
      particles.forEach((particle, index) => {
        const spread = Math.abs(particle.phase - progress);
        const assembled = Math.max(0, 1 - spread * 5.5);
        const gutter = 14 + particle.lane * Math.min(82, window.innerWidth * 0.065);
        const x = particle.side ? window.innerWidth - gutter : gutter;
        const roamingY = particle.phase * window.innerHeight * 1.35 - window.innerHeight * 0.18;
        const y = roamingY * (1 - assembled) + (formationLine + (index % 9 - 4) * 5) * assembled;
        context.beginPath();
        context.fillStyle = index % 5 === 0
          ? `rgba(242, 138, 46, ${0.08 + assembled * 0.28})`
          : `rgba(247, 245, 239, ${0.035 + assembled * 0.16})`;
        context.arc(x, y, particle.size + assembled * 0.8, 0, Math.PI * 2);
        context.fill();
      });
    }

    function requestRender() {
      if (!frame) frame = window.requestAnimationFrame(render);
    }

    const onResize = () => {
      resize();
      requestRender();
    };
    resize();
    requestRender();
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    motionQuery.addEventListener('change', requestRender);

    return {
      destroy() {
        destroyed = true;
        window.cancelAnimationFrame(frame);
        window.removeEventListener('scroll', requestRender);
        window.removeEventListener('resize', onResize);
        motionQuery.removeEventListener('change', requestRender);
        root.removeAttribute('data-pmx-canvasui-particle-scroll');
        canvas.remove();
      },
    };
  }

  // A CanvasUI ASCII-Sweep-inspired transition across the whole interactive
  // experience (dock + media + product/service rail). It is an overlay only:
  // real HTML remains in place, the band clears after one short transition,
  // and reduced-motion visitors get an immediate panel swap with no effect.
  function createModeSweep(root) {
    const experience = root.querySelector('.pmx-h6-front__experience');
    if (!experience) return { destroy() {} };
    const canvas = document.createElement('canvas');
    canvas.className = 'pmx-canvasui-mode-sweep';
    canvas.setAttribute('aria-hidden', 'true');
    experience.appendChild(canvas);
    const context = canvas.getContext('2d', { alpha: true });
    if (!context) {
      canvas.remove();
      return { destroy() {} };
    }

    const SWEEP_DURATION = 1180;
    let width = 0;
    let height = 0;
    let frame = 0;
    let startTime = 0;
    let direction = 1;
    let mode = 'dog';
    let destroyed = false;

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      width = Math.max(1, experience.clientWidth);
      height = Math.max(1, experience.clientHeight);
      const nextWidth = Math.max(1, Math.round(width * dpr));
      const nextHeight = Math.max(1, Math.round(height * dpr));
      if (canvas.width !== nextWidth) canvas.width = nextWidth;
      if (canvas.height !== nextHeight) canvas.height = nextHeight;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function finish() {
      window.cancelAnimationFrame(frame);
      frame = 0;
      context.clearRect(0, 0, width, height);
      root.dataset.pmxCanvasuiModeSweep = 'idle';
    }

    function randomAt(column, row, salt = 0) {
      const value = Math.sin(column * 91.7 + row * 47.3 + salt * 17.1) * 43758.5453;
      return value - Math.floor(value);
    }

    function render(now) {
      if (destroyed || motionQuery.matches || document.hidden) {
        finish();
        return;
      }
      const progress = Math.min((now - startTime) / SWEEP_DURATION, 1);
      const eased = 1 - (1 - progress) ** 3;
      const lead = direction > 0 ? eased * (width + 160) - 80 : width + 80 - eased * (width + 160);
      const glyphs = MODE_SWEEP_GLYPHS[mode] || MODE_SWEEP_GLYPHS.dog;
      const cell = width < 680 ? 18 : 21;
      const band = Math.max(100, width * 0.17);
      context.clearRect(0, 0, width, height);
      context.save();
      context.font = `600 ${Math.max(12, cell * 0.68)}px Consolas, "Courier New", monospace`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.shadowBlur = 7;

      for (let y = cell / 2, row = 0; y < height; y += cell, row += 1) {
        const tear = (randomAt(row, 3, Math.floor(progress * 18)) - 0.5) * 54;
        const rowLead = lead + tear;
        const first = Math.max(cell / 2, rowLead - band);
        const last = Math.min(width, rowLead + band * 0.38);
        for (let x = first, column = Math.floor(first / cell); x <= last; x += cell, column += 1) {
          const distance = Math.abs(x - rowLead) / band;
          const alpha = Math.max(0, (1 - distance) * (0.3 + randomAt(column, row, 4) * 0.48));
          if (alpha < 0.08 || randomAt(column, row, 7) < 0.16) continue;
          const glyph = glyphs[Math.floor(randomAt(column, row, 11) * glyphs.length) % glyphs.length];
          const colorMoment = randomAt(column, row, Math.floor(now / 150)) > 0.9;
          const accent = mode === 'cat' ? '#78aab5' : mode === 'grooming' ? '#a9d8df' : '#f28a2e';
          context.fillStyle = colorMoment ? `rgba(242,138,46,${alpha})` : `${accent}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
          context.shadowColor = colorMoment ? 'rgba(242,138,46,.55)' : accent;
          context.fillText(glyph, x, y);
        }
      }
      context.restore();
      if (progress >= 1) finish();
      else frame = window.requestAnimationFrame(render);
    }

    function onTransition(event) {
      if (motionQuery.matches) {
        finish();
        return;
      }
      const modes = ['dog', 'cat', 'grooming'];
      const fromIndex = modes.indexOf(event.detail?.from);
      const toIndex = modes.indexOf(event.detail?.to);
      direction = fromIndex < 0 || toIndex < 0 || toIndex >= fromIndex ? 1 : -1;
      mode = event.detail?.to || 'dog';
      resize();
      window.cancelAnimationFrame(frame);
      startTime = performance.now();
      root.dataset.pmxCanvasuiModeSweep = 'active';
      frame = window.requestAnimationFrame(render);
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(experience);
    root.addEventListener('pmx:h6-mode-transition', onTransition);
    resize();
    root.dataset.pmxCanvasuiModeSweep = 'idle';

    return {
      destroy() {
        destroyed = true;
        finish();
        resizeObserver.disconnect();
        root.removeEventListener('pmx:h6-mode-transition', onTransition);
        root.removeAttribute('data-pmx-canvasui-mode-sweep');
        canvas.remove();
      },
    };
  }

  function createHeroScene(root) {
    const canvas = root.querySelector('[data-pmx-h6-hero-canvas]');
    if (!canvas) return null;
    let gl;
    try {
      gl = canvas.getContext('webgl2', {
        alpha: true,
        depth: false,
        stencil: false,
        antialias: false,
        premultipliedAlpha: true,
        powerPreference: 'low-power',
      });
    } catch {
      gl = null;
    }
    if (!gl || gl.isContextLost()) {
      root.dataset.pmxCanvasuiScenes = 'fallback';
      return null;
    }

    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vertexShader || !fragmentShader) {
      vertexShader && gl.deleteShader(vertexShader);
      fragmentShader && gl.deleteShader(fragmentShader);
      root.dataset.pmxCanvasuiScenes = 'fallback';
      return null;
    }
    const program = gl.createProgram();
    if (!program) return null;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      root.dataset.pmxCanvasuiScenes = 'fallback';
      return null;
    }

    const uniforms = {};
    const uniformCount = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
    for (let index = 0; index < uniformCount; index += 1) {
      const info = gl.getActiveUniform(program, index);
      if (info) uniforms[info.name.replace('[0]', '')] = gl.getUniformLocation(program, info.name);
    }
    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));

    let imageWidth = 0;
    let imageHeight = 0;
    let imageUrl = '';
    let token = 0;
    let frame = 0;
    let visible = true;
    let destroyed = false;
    let pointer = { x: 0.55, y: 0.52, active: 0 };
    const autoPointer = {
      x: 0.1,
      y: 0.5,
      targetX: 0.9,
      targetY: 0.5,
      side: 1,
      lastFrame: 0,
      nextSwitch: 0,
    };

    const currentMode = () => ({ dog: 0, cat: 1, grooming: 2 }[root.dataset.mode] ?? 0);

    function chooseCatSweepTarget(now) {
      autoPointer.side = autoPointer.side ? 0 : 1;
      autoPointer.targetX = autoPointer.side ? 0.9 : 0.1;
      autoPointer.targetY = 0.24 + Math.random() * 0.52;
      autoPointer.nextSwitch = now + 2400 + Math.random() * 1400;
      root.dataset.pmxCanvasuiCatTarget = `${autoPointer.side ? 'right' : 'left'}:${autoPointer.targetY.toFixed(2)}`;
    }

    function updateCatSweep(now) {
      if (!autoPointer.nextSwitch) {
        autoPointer.nextSwitch = now + 2400 + Math.random() * 1400;
        root.dataset.pmxCanvasuiCatTarget = `right:${autoPointer.targetY.toFixed(2)}`;
      } else if (now >= autoPointer.nextSwitch) {
        chooseCatSweepTarget(now);
      }
      const elapsed = autoPointer.lastFrame ? Math.min((now - autoPointer.lastFrame) / 1000, 0.1) : 0;
      autoPointer.lastFrame = now;
      const follow = 1 - Math.exp(-elapsed * 1.15);
      autoPointer.x += (autoPointer.targetX - autoPointer.x) * follow;
      autoPointer.y += (autoPointer.targetY - autoPointer.y) * follow;
    }

    function clear() {
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      const width = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const height = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
    }

    function syncImage() {
      const media = canvas.parentElement?.closest('.pmx-h6-front__media');
      const source = media?.querySelector(':scope > img');
      const url = source?.currentSrc || source?.src || '';
      if (!url || (url === imageUrl && imageWidth && imageHeight)) return;
      const imageToken = ++token;
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => {
        if (destroyed || imageToken !== token) return;
        try {
          gl.bindTexture(gl.TEXTURE_2D, texture);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
          imageWidth = image.naturalWidth || image.width;
          imageHeight = image.naturalHeight || image.height;
          imageUrl = url;
          root.dataset.pmxCanvasuiScenes = 'active';
        } catch {
          imageWidth = 0;
          imageHeight = 0;
          root.dataset.pmxCanvasuiScenes = 'overlay';
        }
      };
      image.onerror = () => {
        if (imageToken === token) root.dataset.pmxCanvasuiScenes = 'overlay';
      };
      image.src = url;
    }

    function render(now) {
      if (destroyed || !visible || document.hidden || motionQuery.matches) {
        frame = 0;
        clear();
        return;
      }
      resize();
      syncImage();
      const cssWidth = Math.max(canvas.clientWidth, 1);
      const cssHeight = Math.max(canvas.clientHeight, 1);
      let scaleX = 1;
      let scaleY = 1;
      let offsetX = 0;
      let offsetY = 0;
      if (imageWidth && imageHeight) {
        const cover = Math.max(cssWidth / imageWidth, cssHeight / imageHeight);
        scaleX = cssWidth / (imageWidth * cover);
        scaleY = cssHeight / (imageHeight * cover);
        offsetX = (1 - scaleX) * 0.5;
        offsetY = (1 - scaleY) * 0.5;
      }
      const mode = currentMode();
      if (mode === 1) updateCatSweep(now);
      const shaderPointer = mode === 1 && !pointer.active ? autoPointer : pointer;
      const shaderPointerActive = mode === 1 ? 1 : pointer.active;
      gl.useProgram(program);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(uniforms.uContent, 0);
      gl.uniform1f(uniforms.uHasContent, imageWidth && imageHeight ? 1 : 0);
      gl.uniform2f(uniforms.uResolution, canvas.width, canvas.height);
      gl.uniform2f(uniforms.uPointer, shaderPointer.x, shaderPointer.y);
      gl.uniform1f(uniforms.uPointerActive, shaderPointerActive);
      gl.uniform1f(uniforms.uTime, now / 1000);
      gl.uniform1i(uniforms.uMode, mode);
      gl.uniform2f(uniforms.uCoverScale, scaleX, scaleY);
      gl.uniform2f(uniforms.uCoverOffset, offsetX, offsetY);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      frame = window.requestAnimationFrame(render);
    }

    function start() {
      if (!frame && visible && !motionQuery.matches) frame = window.requestAnimationFrame(render);
    }

    function onPointer(event) {
      const media = event.target.closest?.('.pmx-h6-front__media');
      if (!media || media !== canvas.parentElement) return;
      const bounds = canvas.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      pointer = {
        x: Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 1),
        y: 1 - Math.min(Math.max((event.clientY - bounds.top) / bounds.height, 0), 1),
        active: 1,
      };
    }

    function onPointerLeave() {
      pointer.active = 0;
    }

    function onModeChange(event) {
      if (!event.target.closest?.('[data-pmx-h6-mode]')) return;
      pointer.active = 0;
      window.queueMicrotask(() => {
        imageWidth = 0;
        imageHeight = 0;
        imageUrl = '';
        syncImage();
        resize();
        start();
      });
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    const intersectionObserver = new IntersectionObserver((entries) => {
      visible = entries[entries.length - 1]?.isIntersecting ?? true;
      if (visible) start();
      else {
        window.cancelAnimationFrame(frame);
        frame = 0;
        clear();
      }
    }, { threshold: 0.01 });
    intersectionObserver.observe(canvas);
    const mutationObserver = new MutationObserver(() => window.queueMicrotask(syncImage));
    mutationObserver.observe(root, { subtree: true, attributes: true, attributeFilter: ['data-active', 'hidden'] });

    const onVisibility = () => document.hidden ? clear() : start();
    const onMotion = () => motionQuery.matches ? clear() : start();
    const onContextLost = (event) => {
      event.preventDefault();
      window.cancelAnimationFrame(frame);
      frame = 0;
      root.dataset.pmxCanvasuiScenes = 'fallback';
    };
    root.addEventListener('pointermove', onPointer, { passive: true });
    root.addEventListener('pointerdown', onPointer, { passive: true });
    root.addEventListener('pointerleave', onPointerLeave, { passive: true });
    root.addEventListener('click', onModeChange);
    document.addEventListener('visibilitychange', onVisibility);
    motionQuery.addEventListener('change', onMotion);
    canvas.addEventListener('webglcontextlost', onContextLost);
    resize();
    syncImage();
    start();
    root.dataset.pmxCanvasuiScenes = 'ready';

    return {
      destroy() {
        destroyed = true;
        window.cancelAnimationFrame(frame);
        resizeObserver.disconnect();
        intersectionObserver.disconnect();
        mutationObserver.disconnect();
        root.removeEventListener('pointermove', onPointer);
        root.removeEventListener('pointerdown', onPointer);
        root.removeEventListener('pointerleave', onPointerLeave);
        root.removeEventListener('click', onModeChange);
        document.removeEventListener('visibilitychange', onVisibility);
        motionQuery.removeEventListener('change', onMotion);
        canvas.removeEventListener('webglcontextlost', onContextLost);
        gl.deleteTexture(texture);
        gl.deleteBuffer(quad);
        gl.deleteProgram(program);
        gl.deleteShader(vertexShader);
        gl.deleteShader(fragmentShader);
        root.removeAttribute('data-pmx-canvasui-scenes');
      },
    };
  }

  function init(root) {
    if (!root || instances.has(root)) return;
    const hero = createHeroScene(root);
    const modeSweep = createModeSweep(root);
    const particles = createParticleScroll(root);
    instances.set(root, {
      destroy() {
        hero?.destroy();
        modeSweep.destroy();
        particles.destroy();
      },
    });
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  }

  function destroyWithin(scope) {
    for (const [root, instance] of instances) {
      if (root === scope || scope.contains?.(root)) {
        instance.destroy();
        instances.delete(root);
      }
    }
  }

  window.PMXCanvasUIScenes = { init: initAll, destroy: destroyWithin };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
